import { produce } from "immer";
import { z } from "zod";
import { doubleEncode, makeGetRequest } from "../../utils";

type ListSessionsUsersQosOptions = {
	sessionId: string;
	type?: "past" | "live";
	page_size?: number;
	next_page_token?: string;
};

const QosDetail = z.object({
	bitrate: z.string(),
	latency: z.string(),
	jitter: z.string(),
	avg_loss: z.string(),
	max_loss: z.string(),
	resolution: z.optional(z.string()),
	frame_rate: z.optional(z.string()),
});

const CpuUsage = z.object({
	zoom_min_cpu_usage: z.string(),
	zoom_avg_cpu_usage: z.string(),
	zoom_max_cpu_usage: z.string(),
	system_max_cpu_usage: z.string(),
});

const UserQos = z.object({
	date_time: z.string(),
	audio_input: QosDetail,
	audio_output: QosDetail,
	video_input: QosDetail,
	video_output: QosDetail,
	as_input: QosDetail,
	as_output: QosDetail,
	audio_device_from_rwg: QosDetail,
	audio_device_to_rwg: QosDetail,
	video_device_from_rwg: QosDetail,
	video_device_to_rwg: QosDetail,
	as_device_from_rwg: QosDetail,
	as_device_to_rwg: QosDetail,
	cpu_usage: CpuUsage,
});

const User = z.object({
	id: z.string(),
	name: z.string(),
	device: z.string(),
	ip_address: z.string(),
	location: z.string(),
	network_type: z.string(),
	microphone: z.string(),
	speaker: z.string(),
	camera: z.string(),
	data_center: z.string(),
	connection_type: z.string(),
	join_time: z.string(),
	leave_time: z.string(),
	user_qos: z.array(UserQos),
});

const ResponseSchema = z
	.object({
		page_size: z.number(),
		next_page_token: z.string(),
		users: z.array(User),
	})
	.deepPartial();
type Response = z.infer<typeof ResponseSchema>;
const listSessionsUsersQos = async (
	token: string,
	options: ListSessionsUsersQosOptions,
): Promise<Response> => {
	const res = await makeGetRequest({
		token,
		pathList: [
			"v2",
			"videosdk",
			"sessions",
			doubleEncode(options.sessionId),
			"users",
			"qos",
		],
		options: produce(options, (draft) => {
			if (!draft.type) {
				draft.type = "live";
			}
		}),
	});
	return ResponseSchema.parse(res);
};
export const curriedListSessionsUsersQos = (token: string) => (
	options: ListSessionsUsersQosOptions,
) => listSessionsUsersQos(token, options);